import React, { useState, useEffect, useRef, useReducer, useMemo } from 'react';
import { Loader2, CheckCircle2, RotateCcw, Pause, Play, FastForward } from 'lucide-react';
import { useSpeechCapture } from './useSpeechCapture';
import { scaleRunnerReducer, initialState } from './scaleRunnerReducer';
import { itemTypeRegistry } from './itemTypes/registry';
import ModeSelector from './itemTypes/shared/ModeSelector';
import { ClapDetector } from '../services/audio/clapDetector';
import { prewarmCamera, releaseWarmCamera } from '../services/vision/cameraPrewarm';
import { prewarmSession, setNextCvTarget, clearNextCvTarget, releaseSession, preloadAudio, CV_SESSION_PRECONNECT } from '../services/vision/sessionPrewarm';

// Телефон/планшет (основной указатель — палец, pointer: coarse): в задании
// vigilance вместо хлопка пациент касается экрана (ClapDetector / getUserMedia /
// AudioContext на мобильных ненадёжны). ПК и тачскрин-ноутбуки (основной
// указатель — мышь/тачпад) остаются на хлопках.
const isTouchDevice = () =>
  typeof window !== 'undefined' &&
  !!window.matchMedia?.('(pointer: coarse)')?.matches;

// Текст инструкции для touch-режима по умолчанию. Клиника может переопределить
// его полем askTouch в question_bank (без деплоя).
const DEFAULT_TOUCH_INSTRUCTION =
  'Сейчас вы услышите несколько букв. Коснитесь экрана в любом месте, когда услышите букву А';

export default function ScaleRunner({ scale, onComplete, taskNumber, showControls = false, userId }) {
  const [state, dispatch] = useReducer(scaleRunnerReducer, initialState);
  const [taskState, setTaskState] = useState({});
  const [micToast, setMicToast] = useState(false);
  const micToastTimerRef = useRef(null);
  const showMicToast = (text = 'Вас не слышно. Проверьте микрофон') => {
    setMicToastText(text);
    setMicToast(true);
    clearTimeout(micToastTimerRef.current);
    micToastTimerRef.current = setTimeout(() => setMicToast(false), 3000);
  };
  useEffect(() => () => clearTimeout(micToastTimerRef.current), []);
  const taskStateRef = useRef({});
  const lastQuestionIdRef = useRef(null);
  const clapDetectorRef = useRef(null);
  const letterOnsetRef = useRef(0);               // когда началась текущая буква (performance.now)
  const commissionForLetterRef = useRef(false);   // лишний хлопок на этой букве уже засчитан
  const currentVigilanceLetterRef = useRef('');
  const letterTimeoutRef = useRef(null);
  const vigilanceStatsRef = useRef({ hits: 0, omissions: 0, commissions: 0 });
  const clappedForCurrentLetterRef = useRef(false);
  const touchHandlerRef = useRef(null);           // { type, fn } window-листенер тапа (touch-режим vigilance)
  const vigilanceInputRef = useRef('clap');       // 'clap' | 'touch' | 'pointer' (запасной режим без микрофона)
  const [micToastText, setMicToastText] = useState('');
  const forceFinishRef = useRef(null);
  const question = scale.questions[state.currentStep];
  // Эффективный режим вопроса: принудительно-голосовые задания (чтение,
  // повторение, CV-пробы) всегда работают голосом/камерой, но НЕ сбрасывают
  // режим, выбранный врачом в попапе, — выбор «Показать» доживает до конца
  // шкалы, а такие задания просто временно работают голосом.
  const isForceSay = !!question && (question.forceSay || ['reading', 'repetition', 'memory_cascade', 'memory', 'vigilance', 'drawing', 'trail_making', 'text_input', 'manual_action', 'reading_action', 'stroop', 'cv_face_action'].includes(question?.type));
  const viewMode = question?.type === 'cv_face_action' ? 'clinical' : (isForceSay ? 'say' : (state.mode || 'say'));

  const handleResultRef = useRef(() => {});
  const handleSilenceRef = useRef(() => {});

  const instructionRepeatsRef = useRef({});
  const registerInstructionRepeat = (questionId) => {
    const prev = instructionRepeatsRef.current[questionId] || 0;
    instructionRepeatsRef.current[questionId] = prev + 1;
  };

  const getSilenceTimeout = (q) => {
    if (!q) return 5000;
    if (q.type === 'consciousness') return 7000; // 1а NIHSS: 7 секунд на попытку
    if (q.showTimer) return 999999;
    if (['drawing', 'trail_making', 'text_input', 'manual_action', 'reading_action', 'coding', 'coding_v2', 'vigilance', 'stroop'].includes(q.type)) return 999999;
    if (q.type === 'voice_serial') return 20000;
    // 7 с — только MoCA (5а/9а память, 7а/7б повторение): запрос врачей.
    // MMSE 7а тоже voice_hidden — там оставляем 10 с (пожилые/деменция).
    // Шкалу различаем по имени (в БД может быть 'MoCA'/'MOCA'/'moca'),
    // с запасом принимаем оба написания.
    if (['voice_hidden', 'memory_cascade', 'memory'].includes(q.type)) {
      const scaleName = String(scale?.name || '').toLowerCase();
      return scaleName === 'moca' ? 7000 : 10000;
    }
    return 5000;
  };

  const silenceTimeout = getSilenceTimeout(question);
  console.log('[TIMEOUT] scale:', scale?.name, 'type:', question?.type, 'timeout:', silenceTimeout);

  const { status: hookStatus, transcript, finalTranscript, speak, playAudio, startListening, stopAll } = useSpeechCapture({
    onResult: (final, interim) => handleResultRef.current(final, interim),
    onSilenceTimeout: () => handleSilenceRef.current(),
    onFatal: (err) => console.error('[ASR FATAL] Распознавание недоступно:', err),
    silenceTimeout,
  });

  useEffect(() => {
    if (viewMode !== 'say') return;
    if (['processing', 'paused', 'finished', 'show_options'].includes(state.status)) return;
    if (hookStatus === 'speaking' && state.status !== 'speaking') dispatch({ type: 'SET_STATUS', payload: 'speaking' });
    else if (hookStatus === 'listening' && state.status !== 'listening') dispatch({ type: 'SET_STATUS', payload: 'listening' });
  }, [hookStatus, state.status, state.mode]);

  const ctx = useMemo(() => ({
    next: (rawAnswer, points = 0) => {
      // Тост «Вас не слышно»: молчание/пустой ответ в голосовых заданиях.
      // memory/memory_cascade исключены (объектные ответы, поле на экране).
      // voice_serial отдаёт 'Нет ответа' — тоже считаем молчанием.
      const VOICE_TYPES = ['voice', 'voice_hidden', 'voice_serial', 'repetition', 'reading'];
      const answerStr = typeof rawAnswer === 'object' ? '' : String(rawAnswer).trim();
      const isEmptyVoiceAnswer = VOICE_TYPES.includes(question.type)
        && (!answerStr || answerStr === 'Ответ дан' || answerStr === 'Нет ответа'
            || answerStr.includes('не распознан'));
      if (isEmptyVoiceAnswer) showMicToast();

      console.log("[SR] ctx.next: шаг", state.currentStep, "балл:", points);
      stopAll();
      dispatch({ type: 'SET_STATUS', payload: 'processing' });

      setTimeout(() => {
        const isLast = state.currentStep >= scale.questions.length - 1;

        dispatch({ type: 'RECORD_ANSWER', payload: { questionId: question.id, answer: rawAnswer, points } });

        if (isLast) {
          dispatch({ type: 'FINISH' });
          onComplete({
            rawAnswers: { ...state.rawAnswers, [question.id]: rawAnswer },
            totalScore: state.score + points,
            instructionRepeats: instructionRepeatsRef.current
          });
        } else {
          taskStateRef.current = {};
          setTaskState({});
          dispatch({ type: 'NEXT_STEP' });
        }
      }, 500);
    },

    getTaskState: () => taskStateRef.current,
    setTaskState: (updater) => {
      taskStateRef.current = typeof updater === 'function' ? updater(taskStateRef.current) : updater;
      setTaskState({ ...taskStateRef.current });
    },
    speak: (text, onEnd) => speak(text, onEnd),
    playAudio: (url, onEnd) => playAudio(url, onEnd),
    startListening: () => startListening(),
    stopAll: () => stopAll(),
  }), [question, state.currentStep, state.score, state.rawAnswers, onComplete, speak, playAudio, startListening, stopAll]);

  useEffect(() => {
    if (question?.showTimer && state.status === 'listening') {
      const timer = setTimeout(() => {
        const answer = ctx.getTaskState()?.answer?.trim() || 'Время вышло';
        ctx.next(answer, 0);
      }, question.showTimer * 1000);
      return () => clearTimeout(timer);
    }
  }, [question, state.status, ctx]);

  useEffect(() => {
    if (!question) return;
    const typeData = itemTypeRegistry[question.type];
    if (!typeData) return;
    handleResultRef.current = (finalText, interimText) => typeData.onRecognitionResult(finalText, interimText, question, ctx);
    handleSilenceRef.current = () => typeData.onSilenceTimeout(question, ctx, state.mode); // 1а: колбэк видит режим (say/show)
  }, [question, ctx]);

  // Снимает touch-листенер (если есть). Вызывается из stopVigilance и при
  // перезапуске задания («Повторить»), чтобы листенеры не копились.
  const removeTouchListener = () => {
    if (touchHandlerRef.current) {
      const { type, fn } = touchHandlerRef.current;
      window.removeEventListener(type, fn);
      touchHandlerRef.current = null;
    }
  };

  // Ответ пациента касанием: 'touchstart' на телефоне/планшете, 'pointerdown'
  // в запасном режиме на ПК без микрофона. Тапы по кнопкам не считаются.
  const attachTapListener = (type) => {
    removeTouchListener();
    const fn = (e) => {
      // Мышь: только левая кнопка (правый/средний клик — не ответ пациента)
      if (type === 'pointerdown' && e.button !== 0) return;
      if (e?.target?.closest?.('button')) return;
      handleVigilanceClap(null, true);
    };
    touchHandlerRef.current = { type, fn };
    window.addEventListener(type, fn, { passive: true });
  };

  // Инструкция touch-режима: файл *_tap.mp3 (если залит), иначе синтез текста
  // askTouch (или стандартной фразы). Обычный audioUrl («хлопните») не играем.
  const playTouchInstruction = (q, text, onEnd) => {
    // Суффикс _tap клеим только к .mp3; иначе (другой формат) сразу синтез —
    // обычный audioUrl с инструкцией про хлопки на планшет не уходит.
    const tapAudio = (q.audioUrl && /\.mp3$/i.test(q.audioUrl))
      ? q.audioUrl.replace(/\.mp3$/i, '_tap.mp3')
      : null;
    if (!tapAudio) { speak(text, onEnd); return; }
    let decided = false;
    const play = (useTap) => {
      if (decided) return;
      decided = true;
      if (useTap) playAudio(tapAudio, onEnd);
      else speak(text, onEnd);
    };
    const probe = new Audio();
    probe.preload = 'metadata';
    probe.onloadedmetadata = () => play(true);
    probe.onerror = () => play(false);
    setTimeout(() => play(false), 4000);
    probe.src = tapAudio;
  };

  const stopVigilance = () => {
    if (letterTimeoutRef.current) { clearTimeout(letterTimeoutRef.current); letterTimeoutRef.current = null; }
    // Критично: иначе тапы продолжат считаться в следующих заданиях.
    removeTouchListener();
    if (clapDetectorRef.current) {
      // На мобильных AudioContext может быть suspended/interrupted и stop() бросает —
      // исключение не должно блокировать переход к следующему заданию.
      try { clapDetectorRef.current.stop(); }
      catch (e) { console.error('[VIGILANCE] clapDetector.stop() упал:', e); }
      clapDetectorRef.current = null;
    }
    currentVigilanceLetterRef.current = '';
    vigilanceStatsRef.current = { hits: 0, omissions: 0, commissions: 0 };
    clappedForCurrentLetterRef.current = false;
    commissionForLetterRef.current = false;
  };

  // ===== Ответы пациента на букву «А» (хлопок или тап): общая логика =====
  // Раньше обработчик хлопка и запуск задания были скопированы в двух местах
  // (при открытии вопроса и при «Повторить»). Теперь это одна функция.
  const LETTER_GAP_MS = 1000;      // пауза между буквами
  // Абсолютный порог громкости. Детектор дополнительно сравнивает звук с фоном
  // комнаты и проверяет, что он быстро затухает (см. clapDetector.js).
  // Хлопки не засчитываются (тихие) → уменьшать; лишние хлопки → увеличивать.
  // Порог снижен с 0.03 по полевым данным: тихие хлопки (пожилые пациенты) лежат в 0.015–0.03; фон стабильно 0.005, ratio-проверка (4×фон) остаётся главным фильтром ложных срабатываний
  const CLAP_THRESHOLD = 0.02;
  const CLAP_MIN_INTERVAL_MS = 300;
  // Хлопок раньше этого времени после начала буквы не может быть реакцией
  // на неё (человек не реагирует быстрее ~250 мс). Чаще всего это эхо/щелчок
  // самого звука буквы — на новую букву его не относим. К тапу не относится.
  const CLAP_BLANK_MS = 300;
  // Пауза после последней буквы на touch-устройствах (тап мгновенный, «поздних» нет)
  const TOUCH_FINISH_DELAY_MS = 1500;
  // Окно ожидания хлопков после последней буквы
  const CLAP_FINISH_DELAY_MS = 10000;

  const handleVigilanceClap = (info, isTouch = false) => {
    const currentLetter = currentVigilanceLetterRef.current;
    const sinceOnset = Math.round(performance.now() - letterOnsetRef.current);
    const what = isTouch ? 'ТАП' : 'ХЛОПОК';
    if (currentLetter === '') {
      console.log(`${what} вне буквы (пауза/конец) — не считаем`);
      return;
    }
    if (!isTouch && sinceOnset < CLAP_BLANK_MS) {
      console.log(`ХЛОПОК через ${sinceOnset} мс после начала "${currentLetter}" — слишком рано, не считаем`);
      return;
    }
    console.log(`${what}! Буква: ${currentLetter}, через ${sinceOnset} мс после её начала, громкость ${info?.volume?.toFixed?.(3)}, фон ${info?.floor?.toFixed?.(3)}. Уже отвечали? ${clappedForCurrentLetterRef.current}`);

    if (currentLetter === 'А') {
      if (!clappedForCurrentLetterRef.current) {
        clappedForCurrentLetterRef.current = true;
        vigilanceStatsRef.current.hits++;
        console.log(" -> HIT (правильный ответ)");
      } else {
        console.log(" -> повторный ответ на ту же А — игнорируем");
      }
    } else if (!commissionForLetterRef.current) {
      // Один лишний ответ на букву — максимум. Двойной/эхо-хлопок (или двойной тап)
      // на одной и той же букве — это одна ошибка, а не две.
      commissionForLetterRef.current = true;
      vigilanceStatsRef.current.commissions++;
      console.log(" -> COMMISSION (лишний ответ)");
    } else {
      console.log(" -> ещё один ответ на ту же букву — игнорируем");
    }
  };

  const startVigilanceRun = (q) => {
    if (letterTimeoutRef.current) { clearTimeout(letterTimeoutRef.current); letterTimeoutRef.current = null; }
    removeTouchListener();   // «Повторить»: не навешиваем второй листенер поверх первого
    if (clapDetectorRef.current) {
      try { clapDetectorRef.current.stop(); }
      catch (e) { console.error('[VIGILANCE] clapDetector.stop() упал:', e); }
      clapDetectorRef.current = null;
    }
    currentVigilanceLetterRef.current = '';
    vigilanceStatsRef.current = { hits: 0, omissions: 0, commissions: 0 };
    clappedForCurrentLetterRef.current = false;
    commissionForLetterRef.current = false;

    const touch = isTouchDevice();
    vigilanceInputRef.current = touch ? 'touch' : 'clap';
    // Способ ответа и (для touch) текст инструкции — во View задания.
    // Функциональная форма, чтобы не затирать остальное состояние (currentLetter).
    const instructionText = q.askTouch || DEFAULT_TOUCH_INSTRUCTION;
    ctx.setTaskState(prev => ({
      ...(prev || {}),
      inputMode: vigilanceInputRef.current,
      ...(touch ? { displayAsk: instructionText } : {}),
    }));

    if (touch) {
      // Touch: без ClapDetector / getUserMedia / AudioContext. Ответ = касание
      // в любом месте экрана. Листенер вешаем после инструкции, чтобы касания
      // во время неё не считались.
      playTouchInstruction(q, instructionText, () => {
        attachTapListener('touchstart');
        setTimeout(() => speakLetters(q.letters, 0, q.letters_audio || {}), 1000);
      });
      return;
    }

    const useAudio = q.audioUrl;
    const playFn = useAudio ? playAudio : speak;
    const playArg = useAudio || q.ask;

    playFn(playArg, () => {
      // Разрешение на микрофон на ПК уже выдано под ASR в начале сессии;
      // отдельного запроса в 6c не ожидается.
      clapDetectorRef.current = new ClapDetector({
        threshold: CLAP_THRESHOLD,
        minClapInterval: CLAP_MIN_INTERVAL_MS,
        onClap: handleVigilanceClap,
        debug: true   // пишет в консоль причины срабатываний/отказов; на проде можно выключить
      });

      clapDetectorRef.current.start().then(() => {
        clapDetectorRef.current?.setListeningForClap(false);
        setTimeout(() => speakLetters(q.letters, 0, q.letters_audio || {}), 1000);
      }, (err) => {
        // Микрофон/AudioContext недоступен: задание не должно зависнуть и не
        // должно молча дать «0 ошибок = 1 балл». Переходим на касание/клик
        // мышью (input: 'pointer'), врач видит тост.
        console.error('Clap detector error:', err);
        try { clapDetectorRef.current?.stop(); } catch (e) { /* уже сломан */ }
        clapDetectorRef.current = null;
        vigilanceInputRef.current = 'pointer';
        ctx.setTaskState(prev => ({
          ...(prev || {}),
          inputMode: 'pointer',
          displayAsk: 'Микрофон недоступен. Когда услышите букву «А», нажмите кнопку мыши или коснитесь экрана',
        }));
        showMicToast('Микрофон недоступен. Пациент отвечает касанием экрана или кликом мыши');
        attachTapListener('pointerdown');
        setTimeout(() => speakLetters(q.letters, 0, q.letters_audio || {}), 1000);
      });
    });
  };

  // currentVigilanceLetterRef выставляется в onLetterStart — по РЕАЛЬНОМУ
  // событию начала звука (utterance.onstart / audio.onplay), а не в момент
  // вызова: между ними непредсказуемая задержка синтеза речи, из-за которой
  // хлопки на "А" засчитывались как commission на соседнюю букву.
  const speakLetters = (letters, index = 0, lettersAudio = {}) => {
    // Пропуск (omission) для ПРЕДЫДУЩЕЙ буквы — проверяем перед переходом к новой
    if (index > 0) {
      const prevLetter = letters[index - 1];
      if (prevLetter === 'А' && !clappedForCurrentLetterRef.current) {
        vigilanceStatsRef.current.omissions++;
        console.log(`ПРОПУСК! Буква А была пропущена на шаге ${index - 1}`);
      }
    }
    // Между проверкой пропуска и реальным началом следующей буквы активной
    // буквы нет: ответ в этот промежуток не должен ни засчитаться как
    // «второй» на предыдущую А, ни стать «лишним» на новую.
    // Флаги «уже отвечал» сбрасываются в onLetterStart, а не здесь.
    currentVigilanceLetterRef.current = '';

    if (index >= letters.length) {
      // (Пропуск последней буквы уже проверен блоком выше — раньше он
      // здесь считался второй раз, что дало бы двойной пропуск, если бы
      // последней буквой была А.)
      ctx.setTaskState(prev => ({ ...(prev || {}), currentLetter: '' }));

      const inputMode = vigilanceInputRef.current;
      const isClap = inputMode === 'clap';
      if (isClap && clapDetectorRef.current) clapDetectorRef.current.setListeningForClap(true);

      // Касание: +1.5 с и переход (мгновенный ответ). Хлопки: прежнее 10-секундное окно.
      letterTimeoutRef.current = setTimeout(() => {
        const finalVigilanceStats = {
          ...vigilanceStatsRef.current,
          input: inputMode   // 'touch' | 'clap' | 'pointer' — способ ответа в данных
        };

        console.log("СТАТИСТИКА ОТВЕТОВ ПЕРЕД ОТПРАВКОЙ:", finalVigilanceStats);

        // Исключение в stop() не должно блокировать переход (в т.ч. на десктопе)
        try { stopVigilance(); }
        catch (e) { console.error('[VIGILANCE] stopVigilance упал:', e); }
        ctx.next(finalVigilanceStats, 0);
      }, isClap ? CLAP_FINISH_DELAY_MS : TOUCH_FINISH_DELAY_MS);
      return;
    }

    const letter = letters[index];
    const audioUrl = lettersAudio?.[letter] || null;
    const callTime = performance.now();

    // Вызывается по РЕАЛЬНОМУ началу звука — здесь, и только здесь,
    // становится безопасно считать, что пациент "видит" (слышит) именно
    // эту букву, и включать её как текущую цель для детектора хлопков / тапов.
    const onLetterStart = () => {
      const startDelay = performance.now() - callTime;
      console.log(`[VIGILANCE] "${letter}" реально начала звучать через ${startDelay.toFixed(0)}мс после вызова`);
      clappedForCurrentLetterRef.current = false;
      commissionForLetterRef.current = false;
      letterOnsetRef.current = performance.now();
      // Пока из колонок звучит буква — порог детектора строже
      clapDetectorRef.current?.setSpeakerActive?.(true);
      currentVigilanceLetterRef.current = letter;
      ctx.setTaskState(prev => ({ ...(prev || {}), currentLetter: letter }));
      if (clapDetectorRef.current) clapDetectorRef.current.setListeningForClap(true);
    };

    // Пауза между буквами: LETTER_GAP_MS (1 секунда) после того, как
    // предыдущая буква договорена. Раньше было 300 мс + 2000 мс.
    // На touch clapDetectorRef пуст — переход к следующей букве не зависит от него.
    const onLetterEnd = () => {
      clapDetectorRef.current?.setSpeakerActive?.(false);
      letterTimeoutRef.current = setTimeout(() => {
        speakLetters(letters, index + 1, lettersAudio);
      }, LETTER_GAP_MS);
    };

    if (audioUrl) playAudio(audioUrl, onLetterEnd, onLetterStart);
    else speak(letter, onLetterEnd, onLetterStart);
  };

  const playFirstMediaWord = () => {
    const firstItem = question.mediaText[0];
    const text = typeof firstItem === 'string' ? firstItem : firstItem.text;
    const url = typeof firstItem === 'string' ? null : firstItem.audioUrl;
    if (url) playAudio(url);
    else speak(text);
  };

  useEffect(() => {
    if (!question) return;
    if (lastQuestionIdRef.current === question.id) return;

    lastQuestionIdRef.current = question.id;
    stopAll();

    if (question.showModeSelector) {
      dispatch({ type: 'RESET_SECTION' });
      return;
    }

    const isForceSay = question.forceSay || ['reading', 'repetition', 'memory_cascade', 'memory', 'vigilance', 'drawing', 'trail_making', 'text_input', 'manual_action', 'reading_action', 'coding', 'coding_v2', 'stroop', 'cv_face_action'].includes(question?.type);
    const currentMode = isForceSay ? 'say' : (state.mode || 'say');

    // КЛЮЧЕВАЯ СТРОКА: forceSay-вопрос не сбрасывает выбранный врачом режим.
    // После принудительно-голосового задания (например, 9a) голосовые
    // вопросы возвращаются в выбранный режим: «Показать» доживает до 9b.
    if (!isForceSay && state.mode !== currentMode) dispatch({ type: 'SET_MODE', payload: currentMode });

    if (question.type === 'vigilance') {
      startVigilanceRun(question);
      return;
    }

    const textToSpeak = question.ttsText || (question.target ? (currentMode === 'show' ? `Покажите ${question.target}` : (question.ask || 'Что это?')) : question.ask);

    if (question.silent) {
      dispatch({ type: 'SET_STATUS', payload: 'show_options' });
      return;
    }

    const useAudio = question.audioUrl;
    const playFn = useAudio ? playAudio : speak;
    const playArg = useAudio || textToSpeak;

    if (currentMode === 'show') {
      playFn(playArg, () => dispatch({ type: 'SET_STATUS', payload: 'show_options' }));
    } else {
      if (question.type === 'repetition' && Array.isArray(question.mediaText)) {
        playFn(playArg, () => setTimeout(() => playFirstMediaWord(), 500));
      } else if (['drawing', 'trail_making', 'text_input', 'manual_action', 'reading_action', 'coding', 'coding_v2', 'stroop'].includes(question.type)) {
        playFn(playArg, () => dispatch({ type: 'SET_STATUS', payload: 'show_options' }));
      } else if (question.type === 'cv_face_action') {
        // Инструкцию CV-проба озвучивает сама — ПОСЛЕ готовности камеры и
        // сервера (см. start_button_activate в ArticulationGame). Играть её
        // здесь значило бы «сказать и бросить пациента в тишину на ~6 секунд».
      } else {
        playFn(playArg);
      }
    }
  }, [state.currentStep, question, showControls, stopAll, speak, playAudio, state.mode]);

  // ХОД 2: пока пациент отвечает на текущий вопрос, заранее включаем камеру
  // под следующий CV-пункт шкалы (cv_face_action). Тихо, в фоне.
  useEffect(() => {
    const qs = scale?.questions || [];
    const nextQ = qs[state.currentStep + 1];
    if (nextQ?.type === "cv_face_action") {
      setNextCvTarget(nextQ.exerciseConfig, userId ?? null); // окно cv→cv (4a→4b→4c)
      preloadAudio(nextQ.audioUrl);
    } else {
      clearNextCvTarget();
    }
    // ХОД 4: ранний старт. Ищем ближайшую CV-пробу в пределах CV_LOOKAHEAD_STEPS
    // вопросов вперёд и греем сессию заранее. Для немедленного старта с начала
    // шкалы поставьте 99.
    if (question?.type !== "cv_face_action") {
      const CV_LOOKAHEAD_STEPS = 4;
      const end = Math.min(qs.length, state.currentStep + 1 + CV_LOOKAHEAD_STEPS);
      let cvQ = null;
      for (let i = state.currentStep + 1; i < end; i++) {
        if (qs[i]?.type === "cv_face_action") { cvQ = qs[i]; break; }
      }
      if (cvQ) {
        if (!CV_SESSION_PRECONNECT) prewarmCamera(); // при включённой сессии камеру открывает она сама
        prewarmSession(cvQ.exerciseConfig, userId ?? null);
        preloadAudio(cvQ.audioUrl);
      }
    }
  }, [state.currentStep, question, scale, userId]);
  const handleSelectMode = (selectedMode) => {
    dispatch({ type: 'SET_MODE', payload: selectedMode });

    // Устная форма («Сказать») может иметь свою инструкцию (текст — question.askSay)
    // и свой аудиофайл: тот же URL из ячейки, но с суффиксом _say
    // (sdmt_2b.mp3 -> sdmt_2b_say.mp3). Файла нет — играем обычный; нет аудио
    // вообще — произносим текст синтезом.
    const baseText = selectedMode === 'show' && question.target
      ? `Покажите ${question.target}`
      : question.ask;
    const textToSpeak = (selectedMode === 'say' && question.askSay)
      ? question.askSay
      : (baseText || 'Что это?');

    const sayAudio = question.audioUrl && selectedMode === 'say'
      ? question.audioUrl.replace(/\.mp3$/i, '_say.mp3')
      : null;

    if (sayAudio) {
      // _say-файл может быть не залит — проверяем доступность и откатываемся на обычный.
      let decided = false;
      const play = (useSay) => {
        if (decided) return;
        decided = true;
        const url = useSay ? sayAudio : question.audioUrl;
        if (selectedMode === 'show' || ['coding', 'coding_v2'].includes(question.type)) {
          playAudio(url, () => dispatch({ type: 'SET_STATUS', payload: 'show_options' }));
        } else {
          playAudio(url);
        }
      };
      const probe = new Audio();
      probe.preload = 'metadata';
      probe.onloadedmetadata = () => play(true);
      probe.onerror = () => play(false);
      setTimeout(() => play(false), 4000);
      probe.src = sayAudio;
      return;
    }

    const useAudio = question.audioUrl;
    const playFn = useAudio ? playAudio : speak;
    const playArg = useAudio || textToSpeak;
    if (selectedMode === 'show' || ['coding', 'coding_v2'].includes(question.type)) playFn(playArg, () => dispatch({ type: 'SET_STATUS', payload: 'show_options' }));
    else playFn(playArg);
  };

  const handlePause = () => { dispatch({ type: 'PAUSE' }); stopAll(); };

  const handleRepeat = () => {
    if (!question) return;
    registerInstructionRepeat(question.id);

    if (question.type === 'vigilance') {
      startVigilanceRun(question);
      return;
    }

    const textToSpeak = question.ttsText || (question.target ? (question.ask || 'Что это?') : question.ask);
    const useAudio = question.audioUrl;
    const playFn = useAudio ? playAudio : speak;
    const playArg = useAudio || textToSpeak;

    if (state.mode === 'show' || ['drawing', 'trail_making', 'text_input', 'manual_action', 'reading_action', 'coding', 'coding_v2'].includes(question.type)) {
      playFn(playArg, () => dispatch({ type: 'SET_STATUS', payload: 'show_options' }));
    } else {
      if (question.type === 'repetition' && Array.isArray(question.mediaText)) {
        playFn(playArg, () => setTimeout(() => playFirstMediaWord(), 500));
      } else {
        playFn(playArg);
      }
    }
  };

  const handleForceFinish = () => {
    // Для stroop «досрочно» штатно закрывает карточку через компонент —
    // payload уходит через onChoiceClick. Прямой onComplete терял весь
    // тест (rawAnswers без '1a' = {} в scale_reports).
    if (question?.type === 'stroop' && forceFinishRef.current) {
      forceFinishRef.current('manual');
      return;
    }
    stopAll();
    stopVigilance();
    onComplete({
      rawAnswers: state.rawAnswers,
      totalScore: state.score,
      instructionRepeats: instructionRepeatsRef.current
    });
  };

  const handleChoiceClick = (choice, overridePoints) => {
    console.log("[SR] handleChoiceClick вызван:", { type: question?.type, status: state.status });
    // cv_face_action: у cv-пробы нет кнопок и микрофона, и статус шкалы
    // для неё никто не переводит — общий запрет молча глотал результат
    // пробы (экран «Поза не удержана» висел навсегда). Для cv-пробы
    // пропускаем запрет mode_selection; processing не трогаем — он
    // означает «переход уже идёт». Двойной переход невозможен: игра
    // вызывает onChoiceClick ровно один раз (замок answeredRef).
    const blocked = state.status === 'processing'
      || (state.status === 'mode_selection' && question?.type !== 'cv_face_action');
    if (blocked) return;

    if (question.type === 'trail_making') {
      // Если пришел ответ с картинкой (текст||картинка)
      if (typeof choice === 'string' && choice.includes('||')) {
        const [answerText, dataUrl] = choice.split('||');
        const points = answerText === 'Задание выполнено верно' ? 1 : 0;
        // Сохраняем как ответ строку с картинкой
        ctx.next(choice, points);
      } else {
        const points = choice === 'correct' ? 1 : 0;
        const answerText = points === 1 ? 'Задание выполнено верно' : 'Задание выполнено неверно';
        ctx.next(answerText, points);
      }
      return;
    }

    if (question.type === 'memory_cascade') {
      const tState = ctx.getTaskState() || {};
      const found = [...(tState.found || [])];
      const missed = [...(tState.missed || [])];
      let missIdx = tState.missIdx || 0;
      const targetWord = missed[missIdx];

      // НОВОЕ: Если выбрал правильный вариант — 'choice', если неправильный — 'fail'
      if (choice === targetWord && !found.some(f => f.word === targetWord)) {
        found.push({ word: targetWord, status: 'choice' });
      } else if (choice !== targetWord && !found.some(f => f.word === targetWord)) {
        found.push({ word: targetWord, status: 'fail' });
      }

      if (missIdx < missed.length - 1) {
        missIdx++;
        ctx.setTaskState({ found, missed, phase: 'hint', missIdx });
        const nextWord = missed[missIdx];
        const hintObj = question.words.find(w => w.word === nextWord);
        if (hintObj?.hint_audio_url) ctx.playAudio(hintObj.hint_audio_url, () => ctx.startListening());
        else if (hintObj?.hint) ctx.speak(hintObj.hint, () => ctx.startListening());
        else ctx.startListening();
      } else {
        ctx.next(found, found.filter(f => f.status === 'free').length);
      }
      return;
    }

    if (['drawing', 'text_input', 'manual_action', 'reading_action', 'cv_face_action'].includes(question.type)) {
      // cv_face_action больше не приносит балл: в шкале проба фиксирует,
      // ЧТО сделал пациент, а не выносит вердикт «верно/неверно». Оценку
      // даёт врач по выписке. На итог это не влияет и задним числом:
      // серверный скорер NIHSS пункты 4a-4c не считает, а инлайновые
      // баллы раннера TestRunnerPage всё равно отбрасывает.
      ctx.next(choice, 0);
      return;
    }

    if (['coding', 'coding_v2'].includes(question.type)) {
      try {
        const data = JSON.parse(choice);
        ctx.next(choice, data.score || 0);
      } catch { ctx.next(choice, 0); }
      return;
    }

    const isCorrect = choice === question.target || choice === question.correctOption;
    ctx.next(choice, isCorrect ? 1 : 0);
  };

  useEffect(() => { return () => { stopAll(); stopVigilance(); releaseWarmCamera(); }; }, [stopAll]);
  useEffect(() => () => { releaseSession(); }, []); // ХОД 3: шкала закрыта — фоновая сессия и камера не должны жить дальше

  if (state.status === 'finished' || !question) {
    // Пока TestRunnerPage ждёт ответ сервера, показываем «Часть завершена»
    // и индикатор. Окончательный балл врач видит только в отчёте (серверный
    // пересчёт может отличаться от локальной суммы).
    return (
      <div className="h-screen flex flex-col justify-center items-center text-center bg-background">
        <CheckCircle2 className="size-20 text-green-500 mb-6" />
        <p className="text-lg text-muted-foreground">Часть завершена</p>
        <div className="flex items-center gap-2 mt-4 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" />
          <span>Формируем отчёт...</span>
        </div>
      </div>
    );
  }

  const ViewComponent = itemTypeRegistry[question.type]?.View;
  const sectionMatch = String(question.id).match(/(\d+)/);
  const displaySection = sectionMatch ? sectionMatch[1] : taskNumber;
  const showModeSelector = state.status === 'mode_selection' && !state.sectionStarted;
  const showQuestion = !showModeSelector && state.status !== 'paused' && state.status !== 'processing' && ViewComponent;

  return (
    <div className="h-screen flex flex-col bg-background relative touch-manipulation">
      <div className="absolute top-6 left-6 z-30 text-sm font-semibold text-muted-foreground bg-secondary/80 px-3 py-1.5 rounded-full backdrop-blur-sm border border-border">
        Задание №{displaySection}
      </div>

      {!['coding', 'coding_v2'].includes(question.type) && (
        <button onClick={handleForceFinish} className="absolute top-6 right-6 z-30 flex items-center gap-2 text-sm font-semibold text-red-500 bg-red-500/10 px-3 py-1.5 rounded-full backdrop-blur-sm border border-red-500/30 hover:bg-red-500/20 transition-colors">
          <FastForward className="size-4" /> Завершить досрочно
        </button>
      )}

      <div className="flex-grow flex flex-col items-center justify-center p-6 gap-8 overflow-hidden">

        {showQuestion && (
          <ViewComponent
            question={question}
            transcript={transcript}
            finalTranscript={finalTranscript}
            stopAll={stopAll}
            forceFinishRef={forceFinishRef}
            status={state.status}
            taskState={taskState}
            mode={viewMode}
            onChoiceClick={handleChoiceClick}
            startListening={startListening}
            speak={speak}
            playAudio={playAudio}
            exerciseConfig={question.exerciseConfig}
            userId={userId ?? null}
            prewarmAfterClose={scale?.questions?.[state.currentStep + 1]?.type === "cv_face_action"}
          />
        )}

        {showModeSelector && <ModeSelector onSelect={handleSelectMode} />}

        {micToast && (
          <div className="fixed top-20 left-1/2 -translate-x-1/2 z-50 bg-red-500/90 text-white px-6 py-3 rounded-2xl shadow-2xl text-base font-semibold">
            {micToastText}
          </div>
        )}

        {state.status === 'processing' && (
          <div className="flex flex-col items-center z-10 gap-4">
            <Loader2 className="size-12 text-accent animate-spin" />
          </div>
        )}

        {state.status === 'paused' && (
          <div className="flex flex-col items-center z-10">
            <Pause className="size-20 text-primary mb-6" />
            <p className="text-3xl font-semibold text-primary mb-8">Пауза</p>
            <button onClick={handleRepeat} className="flex items-center gap-2 bg-primary text-white px-8 py-4 rounded-full text-lg font-semibold hover:bg-primary/90">
              <Play className="size-6" /> Продолжить
            </button>
          </div>
        )}
      </div>

      {/* Кнопка "Повторить инструкцию" доступна всегда, "Пауза" только если showControls. */}
      {!['coding', 'coding_v2', 'stroop'].includes(question.type) && state.status !== 'finished' && state.status !== 'paused' && (
        <div className="flex gap-4 p-4 md:p-6 bg-background border-t border-border justify-center">
          <button onClick={handleRepeat} className="flex items-center justify-center gap-2 py-3 px-6 md:flex-1 md:max-w-xs bg-accent text-white rounded-xl text-base font-bold hover:bg-accent/90 transition-colors">
            <RotateCcw className="size-5" /> Повторить
          </button>
          {showControls && (
            <button onClick={handlePause} className="flex items-center justify-center gap-2 py-3 px-6 md:flex-1 md:max-w-xs bg-primary text-white rounded-xl text-base font-bold hover:bg-primary/90 transition-colors">
              <Pause className="size-5" /> Пауза
            </button>
          )}
        </div>
      )}
    </div>
  );
}